const CACHE_DURATION_LIST = 2 * 60 * 1000; // 2 minutes (reduced for faster updates)
const CACHE_DURATION_DETAIL = 3 * 60 * 1000; // 3 minutes (reduced for faster updates)
const REQUEST_TIMEOUT = 10_000; // 10 seconds

const cache = new Map();

// Common fetch options to prevent browser caching
const fetchOptions = {
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  },
};

// Add cache-busting timestamp to URLs
const addCacheBuster = (url) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_t=${Date.now()}`;
};

const baseUrl = (process.env.REACT_APP_WORDPRESS_URL || "").replace(/\/$/, "");

const withTimeout = async (promise, timeoutMs, controller) => {
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const result = await promise;
    clearTimeout(timeout);
    return result;
  } catch (error) {
    clearTimeout(timeout);
    throw error;
  }
};

const getCached = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) {
    cache.delete(key);
    return null;
  }
  return entry.data;
};

const setCached = (key, data, durationMs) => {
  cache.set(key, { data, expiry: Date.now() + durationMs });
};

// Clear all cached data - useful when CMS content is updated
export const clearAllCache = () => {
  cache.clear();
  console.log("WordPress API cache cleared");
};

const ensureBaseUrl = () => {
  if (!baseUrl) {
    throw new Error("WordPress base URL is missing. Set REACT_APP_WORDPRESS_URL.");
  }
  return baseUrl;
};

export const fetchBlogPosts = async (page = 1, perPage = 9, useCache = true) => {
  ensureBaseUrl();
  const cacheKey = `posts_${page}_${perPage}`;
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/posts?page=${page}&per_page=${perPage}&_embed=true&status=publish&orderby=date&order=desc`);

  const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
  if (!response.ok) {
    throw new Error(`Failed to fetch posts (status ${response.status}).`);
  }

  const posts = await response.json();
  const totalPages = parseInt(response.headers.get("x-wp-totalpages") || "1", 10) || 1;
  const payload = { posts, totalPages };
  setCached(cacheKey, payload, CACHE_DURATION_LIST);
  return payload;
};

export const fetchBlogPostBySlug = async (slug, useCache = true) => {
  ensureBaseUrl();
  const cacheKey = `post_${slug}`;
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/posts?slug=${encodeURIComponent(slug)}&_embed=true&status=publish`);

  const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
  if (!response.ok) {
    throw new Error(`Failed to fetch post (status ${response.status}).`);
  }

  const data = await response.json();
  const post = Array.isArray(data) ? data[0] : null;
  if (!post) {
    throw new Error("Post not found.");
  }

  setCached(cacheKey, post, CACHE_DURATION_DETAIL);
  return post;
};

export const getFeaturedImage = (post, preferredSize = "medium_large") => {
  if (!post?._embedded?.["wp:featuredmedia"]?.length) return null;
  const media = post._embedded["wp:featuredmedia"][0];
  const sizes = media?.media_details?.sizes;
  if (sizes && sizes[preferredSize]?.source_url) return sizes[preferredSize].source_url;

  const sizeOrder = ["large", "full", "medium", "thumbnail"];
  for (const size of sizeOrder) {
    if (sizes?.[size]?.source_url) return sizes[size].source_url;
  }
  return media?.source_url || null;
};

const decodeHtmlEntities = (html) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
};

export const getExcerpt = (post, maxLength = 150) => {
  const raw = post?.excerpt?.rendered || post?.content?.rendered || "";
  let text = raw.replace(/<[^>]+>/g, "").trim();
  text = decodeHtmlEntities(text);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// ==================== TESTIMONIALS API ====================

export const fetchTestimonials = async (useCache = true) => {
  ensureBaseUrl();
  const cacheKey = "testimonials_all";
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/testimonials?per_page=20&_embed=true&status=publish&orderby=menu_order&order=asc`);

  const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
  if (!response.ok) {
    throw new Error(`Failed to fetch testimonials (status ${response.status}).`);
  }

  const testimonials = await response.json();
  
  // Transform the data to match the expected format
  const formattedTestimonials = testimonials.map((item) => ({
    id: item.id,
    name: decodeHtmlEntities(item.title?.rendered || ""),
    course: item.course || "",
    quote: decodeHtmlEntities(item.quote || ""),
    avatar: item.avatar || getFeaturedImage(item, "medium") || `${process.env.PUBLIC_URL}/Testimonials/default-avatar.jpeg`,
  }));

  setCached(cacheKey, formattedTestimonials, CACHE_DURATION_LIST);
  return formattedTestimonials;
};

// ==================== FEATURES API ====================

export const fetchFeatures = async (useCache = true) => {
  ensureBaseUrl();
  const cacheKey = "features_all";
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/features?per_page=20&status=publish&orderby=menu_order&order=asc`);

  const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
  if (!response.ok) {
    throw new Error(`Failed to fetch features (status ${response.status}).`);
  }

  const features = await response.json();
  
  // Transform the data to match the expected format
  const formattedFeatures = features.map((item) => ({
    id: item.id,
    title: decodeHtmlEntities(item.title?.rendered || ""),
    text: decodeHtmlEntities(item.description || item.content?.rendered?.replace(/<[^>]+>/g, "").trim() || ""),
    icon: item.icon || "FaBookOpen", // Default icon
  }));

  setCached(cacheKey, formattedFeatures, CACHE_DURATION_LIST);
  return formattedFeatures;
};

// ==================== COURSES API ====================

export const fetchCourses = async (useCache = true) => {
  ensureBaseUrl();
  const cacheKey = "courses_all";
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/courses?per_page=20&status=publish&orderby=menu_order&order=asc`);

  const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
  if (!response.ok) {
    throw new Error(`Failed to fetch courses (status ${response.status}).`);
  }

  const courses = await response.json();
  
  // Transform the data to match the expected format
  const formattedCourses = courses.map((item) => ({
    id: item.id,
    name: decodeHtmlEntities(item.title?.rendered || ""),
    description: decodeHtmlEntities(item.description || item.content?.rendered?.replace(/<[^>]+>/g, "").trim() || ""),
    path: item.path || "#",
    badge: item.badge || "", // Optional badge
  }));

  setCached(cacheKey, formattedCourses, CACHE_DURATION_LIST);
  return formattedCourses;
};

// ==================== SITE SETTINGS API ====================

export const fetchSiteSettings = async (useCache = true) => {
  ensureBaseUrl();
  const cacheKey = "site_settings";
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  // This fetches the site settings from a custom endpoint or Options page
  const url = addCacheBuster(`${baseUrl}/site-settings`);

  try {
    const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
    if (!response.ok) {
      throw new Error(`Failed to fetch site settings (status ${response.status}).`);
    }

    const settings = await response.json();
    
    const formattedSettings = {
      email: settings.email || settings.acf?.email || "enquiry@thelinguaalliance.com",
      phone: settings.phone || settings.acf?.phone || "+91 90498 30590",
      whatsapp: settings.whatsapp || settings.acf?.whatsapp || "+919049830590",
      address: settings.address || settings.acf?.address || "VENKATESH SHARVIL, near Paranjape Abhiruchi Parisar, Sterling Nisarga II, Dhayari, Pune, Maharashtra 411041",
      facebook: settings.facebook || settings.acf?.facebook || "https://www.facebook.com/profile.php?id=61583176468896",
      instagram: settings.instagram || settings.acf?.instagram || "https://www.instagram.com/the_lingua_alliance/",
      linkedin: settings.linkedin || settings.acf?.linkedin || "https://www.linkedin.com/company/the-lingua-alliance/",
      youtube: settings.youtube || settings.acf?.youtube || "https://www.youtube.com/@TheLinguaAlliance",
    };

    setCached(cacheKey, formattedSettings, CACHE_DURATION_DETAIL);
    return formattedSettings;
  } catch (error) {
    // Return default values if API fails
    console.warn("Failed to fetch site settings, using defaults:", error.message);
    return {
      email: "enquiry@thelinguaalliance.com",
      phone: "+91 90498 30590",
      whatsapp: "+919049830590",
      address: "VENKATESH SHARVIL, near Paranjape Abhiruchi Parisar, Sterling Nisarga II, Dhayari, Pune, Maharashtra 411041",
      facebook: "https://www.facebook.com/profile.php?id=61583176468896",
      instagram: "https://www.instagram.com/the_lingua_alliance/",
      linkedin: "https://www.linkedin.com/company/the-lingua-alliance/",
      youtube: "https://www.youtube.com/@TheLinguaAlliance",
    };
  }
};

// ==================== METRICS API ====================

export const fetchMetrics = async (useCache = true) => {
  ensureBaseUrl();
  const cacheKey = "site_metrics";
  if (useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const controller = new AbortController();
  const url = addCacheBuster(`${baseUrl}/site-metrics`);

  try {
    const response = await withTimeout(fetch(url, { signal: controller.signal, ...fetchOptions }), REQUEST_TIMEOUT, controller);
    if (!response.ok) {
      throw new Error(`Failed to fetch metrics (status ${response.status}).`);
    }

    const data = await response.json();
    
    // Transform to array format expected by Home.js
    const metrics = [
      {
        label: data.metric1_label || "Students Trained",
        value: parseInt(data.metric1_value, 10) || 5000,
        suffix: data.metric1_suffix || "+",
        sub: data.metric1_sub || "From beginners to advanced speakers",
      },
      {
        label: data.metric2_label || "Certified Trainers",
        value: parseInt(data.metric2_value, 10) || 25,
        suffix: data.metric2_suffix || "+",
        sub: data.metric2_sub || "Language trainers & industry mentors",
      },
      {
        label: data.metric3_label || "Alumni Worldwide",
        value: parseInt(data.metric3_value, 10) || 12,
        suffix: data.metric3_suffix || "+",
        sub: data.metric3_sub || "Countries Where Alumni Are Working/Studying",
      },
      {
        label: data.metric4_label || "Success Rate",
        value: parseInt(data.metric4_value, 10) || 98,
        suffix: data.metric4_suffix || "%",
        sub: data.metric4_sub || "Success Rate in International Exams",
      },
    ];

    setCached(cacheKey, metrics, CACHE_DURATION_DETAIL);
    return metrics;
  } catch (error) {
    console.warn("Failed to fetch metrics, using defaults:", error.message);
    // Return default metrics if API fails
    return [
      {
        label: "Students Trained",
        value: 5000,
        suffix: "+",
        sub: "From beginners to advanced speakers",
      },
      {
        label: "Certified Trainers",
        value: 25,
        suffix: "+",
        sub: "Language trainers & industry mentors",
      },
      {
        label: "Alumni Worldwide",
        value: 12,
        suffix: "+",
        sub: "Countries Where Alumni Are Working/Studying",
      },
      {
        label: "Success Rate",
        value: 98,
        suffix: "%",
        sub: "Success Rate in International Exams",
      },
    ];
  }
};
